import { Alert, Button, DatePicker, Form, Radio, Select, Space, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import ptBR from "antd/lib/date-picker/locale/pt_BR";
import { useContext, useEffect, useState } from "react";
import { cadastroCotaUsuarioApi } from "../cadastroCotaUsuario.api";
import {
  CidadeHospedagem,
  DadosOportunidadesHospedagem,
  FluxoResposta,
} from "../cadastroCotaUsuario.types";
import { getApiErrorMessage, OnboardingFrame, OnboardingNavigationContext, SubmitButton } from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

export interface AlertaFormValues {
  cidadeIds: number[];
  quandoViajar: "QUALQUER_PERIODO" | "EPOCA_EM_MENTE";
  periodo?: [Dayjs, Dayjs];
}

export function criarPayloadAlertaHospedagem(values: AlertaFormValues) {
  return {
    acao: "CRIAR_ALERTA_HOSPEDAGEM",
    cidadeIds: Array.from(new Set(values.cidadeIds)),
    ...(values.quandoViajar === "EPOCA_EM_MENTE" && values.periodo
      ? {
          dataIni: values.periodo[0].format("YYYY-MM-DD"),
          dataFim: values.periodo[1].format("YYYY-MM-DD"),
        }
      : {}),
  };
}

function CriarAlertaHospedagem({ fluxo, submitting, onConcluir }: Props) {
  const [form] = Form.useForm<AlertaFormValues>();
  const [cidades, setCidades] = useState<CidadeHospedagem[]>([]);
  const [carregandoDestinos, setCarregandoDestinos] = useState(true);
  const [erroDestinos, setErroDestinos] = useState<string>();
  const [tentativa, setTentativa] = useState(0);
  const cidadeIds = Form.useWatch("cidadeIds", form) ?? [];
  const quandoViajar = Form.useWatch("quandoViajar", form);
  const dados = fluxo.dadosPreenchidos?.oportunidadesHospedagem as
    DadosOportunidadesHospedagem | undefined;

  useEffect(() => {
    let active = true;
    setCarregandoDestinos(true);
    setErroDestinos(undefined);

    cadastroCotaUsuarioApi.buscarDestinosHospedagem()
      .then((response) => {
        if (active) {
          setCidades(response.filter((cidade) => Number.isInteger(cidade.id) && cidade.id > 0));
        }
      })
      .catch((error) => {
        if (active) {
          setErroDestinos(getApiErrorMessage(error, "Não foi possível carregar os destinos."));
        }
      })
      .finally(() => {
        if (active) setCarregandoDestinos(false);
      });

    return () => { active = false; };
  }, [tentativa]);

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Para onde você gostaria de viajar?"
      description="Escolha os destinos do seu interesse. Quando surgirem oportunidades de hospedagem, avisaremos você."
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ cidadeIds: dados?.cidadeIds ?? [], quandoViajar: "QUALQUER_PERIODO" }}
        disabled={submitting}
        onFinish={(values) => {
          if (submitting) return;
          onConcluir(criarPayloadAlertaHospedagem(values));
        }}
      >
        {erroDestinos ? (
          <Alert
            type="error"
            showIcon
            message={erroDestinos}
            style={{ marginBottom: 20 }}
            action={<Button onClick={() => setTentativa((value) => value + 1)}>Tentar novamente</Button>}
          />
        ) : null}
        <Form.Item
          name="cidadeIds"
          label="Destinos do seu interesse"
          rules={[{
            validator: (_, value: number[] | undefined) =>
              value?.length && value.every((id) => Number.isInteger(id) && id > 0)
                ? Promise.resolve()
                : Promise.reject(new Error("Selecione pelo menos um destino válido.")),
          }]}
        >
          <Select
            mode="multiple"
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Selecione uma ou mais cidades"
            loading={carregandoDestinos}
            disabled={submitting || carregandoDestinos || Boolean(erroDestinos)}
            options={cidades.map((cidade) => ({
              value: cidade.id,
              label: `${cidade.nome} - ${cidade.abreviacao}`,
            }))}
            notFoundContent={carregandoDestinos ? "Carregando destinos..." : "Nenhum destino encontrado"}
          />
        </Form.Item>
        <Form.Item name="quandoViajar" label="Quando pretende viajar?">
          <Radio.Group>
            <Space wrap size={16}>
              <Radio value="QUALQUER_PERIODO">Qualquer período</Radio>
              <Radio value="EPOCA_EM_MENTE">Tenho uma época em mente</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        {quandoViajar === "EPOCA_EM_MENTE" ? (
          <>
            <Form.Item
              name="periodo"
              label="Período da viagem"
              rules={[{
                validator: (_, value: [Dayjs, Dayjs] | undefined) =>
                  value?.[0]?.isValid() && value?.[1]?.isValid() &&
                  !value[0].isBefore(dayjs(), "day") && value[1].isAfter(value[0], "day")
                    ? Promise.resolve()
                    : Promise.reject(new Error("Selecione as datas de ida e volta, com a volta após a ida.")),
              }]}
            >
              <DatePicker.RangePicker
                locale={ptBR}
                format="DD/MM/YYYY"
                placeholder={["Data de ida", "Data de volta"]}
                disabledDate={(data) => data.isBefore(dayjs(), "day")}
                style={{ width: "100%", maxWidth: 480 }}
              />
            </Form.Item>
          </>
        ) : null}
        <SubmitButton
          label="Criar alerta"
          loading={submitting}
          disabled={submitting || carregandoDestinos || Boolean(erroDestinos) || !cidadeIds.length}
          disabledStyle={{
            background: "#486f76",
            borderColor: "#486f76",
            color: "#fff",
          }}
        />
      </Form>
    </OnboardingFrame>
  );
}

export function EtapaOportunidadesHospedagem(props: Props) {
  const { fluxo, submitting, onConcluir } = props;
  const { onVoltar } = useContext(OnboardingNavigationContext);
  const pergunta = fluxo.detalhesEtapa?.pergunta;

  if (pergunta === "CRIAR_ALERTA_HOSPEDAGEM") {
    return <CriarAlertaHospedagem {...props} />;
  }

  if (
    pergunta &&
    pergunta !== "DESEJA_RECEBER_OPORTUNIDADES_HOSPEDAGEM"
  ) {
    return (
      <OnboardingFrame fluxo={fluxo} title="Oportunidades de hospedagem">
        <Alert type="warning" showIcon message="Não foi possível identificar esta etapa. Recarregue a página para retomar seu cadastro." />
      </OnboardingFrame>
    );
  }

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Que tal aproveitar a Multicotas também para viajar?"
      description={
        <>
          Além de aproveitar melhor sua multipropriedade, você também pode encontrar{" "}
          <strong>boas oportunidades de hospedagem</strong> com outros proprietários e hotéis parceiros.
        </>
      }
      compactHero
    >
      <Typography.Title level={4} style={{ margin: "0 0 8px", color: "#003644" }}>
        Quer receber oportunidades para os destinos que deseja conhecer?
      </Typography.Title>
      <Typography.Paragraph style={{ fontSize: 16, marginBottom: 24 }}>
        Conte para onde gostaria de viajar e avisaremos quando surgirem oportunidades de hospedagem nesses destinos.
      </Typography.Paragraph>
      <Space wrap size={12}>
        {onVoltar ? (
          <Button size="large" disabled={submitting} onClick={onVoltar}>
            Voltar
          </Button>
        ) : null}
        <Button
          type="primary"
          size="large"
          loading={submitting}
          disabled={submitting}
          style={{ background: "#003644", borderColor: "#003644" }}
          onClick={() => onConcluir({ desejaReceberOportunidadesHospedagem: true })}
        >
          Criar alerta de viagem
        </Button>
        <Button
          size="large"
          type="text"
          disabled={submitting}
          onClick={() => onConcluir({ desejaReceberOportunidadesHospedagem: false })}
        >
          Agora não
        </Button>
      </Space>
    </OnboardingFrame>
  );
}
